import { useCallback, useEffect, useState } from 'react';
import Page from '../../../../components/page/Page';
import Header from '../../../../components/administration/businesses/Header';
import { IconButton, Paper, Collapse, Box } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
} from '@mui/x-data-grid';
import useThemeMediaQuery from '../../../../../core/hooks/useThemeMediaQuery';
import { useNavigationCore } from '../../../../../core/router/navigation';
import { useMeCore } from '../../../../../core/modules/me';
import { useGlobalCore } from '../../../../../core/modules/global';
import CreateDialog from '../../../../components/administration/businesses/CreateDialog';
import UpdateDialog from '../../../../components/administration/businesses/UpdateDialog';
import CreateAreaDialog from '../../../../components/administration/businesses/CreateAreaDialog';
import UpdateAreaDialog from '../../../../components/administration/businesses/UpdateAreaDialog';
import { useCompanyCore } from '../../../../../core/modules/company';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  UPDATE_BUSINESS_DIALOG,
  CREATE_AREA_DIALOG,
  UPDATE_AREA_DIALOG,
} from '../../../../../core/constants/dialog';
import { useBusinessCore } from '../../../../../core/modules/business';
import {
  Business,
  BusinessArea,
} from '../../../../../core/interfaces/business.interface';
import { calculateUsedCommission } from '../../../../../core/modules/business/helpers/commission-calculator';

// Tipos para las filas jerárquicas
interface BusinessRowData {
  id: string;
  type: 'business' | 'area';
  businessId: number;
  areaId?: number;
  name: string;
  acronym?: string;
  business_name?: string;
  district?: string;
  street?: string;
  number?: string;
  unit?: string;
  commission?: number;
  originalBusiness?: Business;
  parentId?: string;
}

const Businesses = () => {
  const { user } = useMeCore();
  const { openDialog } = useGlobalCore();
  const { companyId } = useCompanyCore();
  const { businesses, setBusiness, getBusinesses } = useBusinessCore();
  const { goToWelcome } = useNavigationCore();

  // Estado para manejar las filas expandidas
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Estado para la empresa seleccionada para crear área
  const [selectedBusinessForArea, setSelectedBusinessForArea] =
    useState<Business | null>(null);

  // Estado para el área seleccionada para editar
  const [selectedBusinessForEditArea, setSelectedBusinessForEditArea] =
    useState<Business | null>(null);
  const [selectedAreaForEdit, setSelectedAreaForEdit] =
    useState<BusinessArea | null>(null);

  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));
  const [gridReady, setGridReady] = useState(false);

  useEffect(() => {
    // Render diferido para evitar ResizeObserver loop al cambiar de vista
    const t = setTimeout(() => setGridReady(true), 0);
    return () => clearTimeout(t);
  }, []);

  // Función para transformar businesses en estructura jerárquica
  const transformBusinessesToRows = useCallback((): BusinessRowData[] => {
    const rows: BusinessRowData[] = [];

    businesses.forEach((business) => {
      const businessRowId = `business-${business.id}`;

      // Agregar fila del business
      rows.push({
        id: businessRowId,
        type: 'business',
        businessId: business.id,
        name: business.name,
        acronym: business.acronym,
        business_name: business.business_name,
        district: business.district,
        street: business.street,
        number: business.number,
        unit: business.unit,
        originalBusiness: business,
      });

      // Si está expandido, agregar las áreas
      if (
        expandedRows.has(businessRowId) &&
        business.areas &&
        business.areas.length > 0
      ) {
        business.areas.forEach((area) => {
          rows.push({
            id: `area-${business.id}-${area.id}`,
            type: 'area',
            businessId: business.id,
            areaId: area.id,
            name: area.name,
            acronym: area.acronym,
            commission: area.commission,
            parentId: businessRowId,
          });
        });
      }
    });

    return rows;
  }, [businesses, expandedRows]);

  // Función para manejar expand/collapse
  const toggleExpanded = useCallback((businessRowId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(businessRowId)) {
        newSet.delete(businessRowId);
      } else {
        newSet.add(businessRowId);
      }
      return newSet;
    });
  }, []);

  useEffect(() => {
    if (
      !user ||
      (!user.privileges.superAdmin &&
        !user.privileges.readBusiness &&
        !user.privileges.writeBusiness)
    ) {
      goToWelcome();
    }
  }, [user, goToWelcome]);

  useEffect(() => {
    if (companyId) {
      getBusinesses(companyId);
    }
  }, [companyId, getBusinesses]);

  const selectBusiness = useCallback(
    (rowData: BusinessRowData) => {
      if (rowData.type === 'business' && rowData.originalBusiness) {
        setBusiness(rowData.originalBusiness);
        openDialog(UPDATE_BUSINESS_DIALOG);
      }
    },
    [openDialog, setBusiness]
  );

  const selectBusinessForArea = useCallback(
    (rowData: BusinessRowData) => {
      if (rowData.type === 'business' && rowData.originalBusiness) {
        setSelectedBusinessForArea(rowData.originalBusiness);
        openDialog(CREATE_AREA_DIALOG);
      }
    },
    [openDialog]
  );

  const selectAreaForEdit = useCallback(
    (rowData: BusinessRowData) => {
      if (rowData.type === 'area' && rowData.businessId && rowData.areaId) {
        // Encontrar la empresa y el área completas
        const business = businesses.find((b) => b.id === rowData.businessId);
        const area = business?.areas?.find((a) => a.id === rowData.areaId);

        if (business && area) {
          setSelectedBusinessForEditArea(business);
          setSelectedAreaForEdit(area);
          openDialog(UPDATE_AREA_DIALOG);
        }
      }
    },
    [businesses, openDialog]
  );

  // Obtener las filas transformadas
  const tableRows = transformBusinessesToRows();

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Nombre',
      flex: 1,
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams<BusinessRowData>) => {
        const row = params.row;
        const isExpanded = expandedRows.has(row.id);
        const hasAreas =
          row.type === 'business' &&
          row.originalBusiness?.areas &&
          row.originalBusiness.areas.length > 0;

        return (
          <Box
            display="flex"
            alignItems="center"
            sx={{
              pl: row.type === 'area' ? 4 : 0,
              width: '100%',
            }}
          >
            {row.type === 'business' && hasAreas && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpanded(row.id);
                }}
                sx={{ mr: 1 }}
              >
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
            <div
              style={{
                marginLeft: row.type === 'business' && !hasAreas ? '40px' : '0',
              }}
            >
              <div className="text-semibold">
                {row.type === 'area' ? `• ${row.name}` : row.name}
              </div>
              {row.type === 'business' && (
                <div className="text-gray-400">
                  {row.business_name}
                </div>
              )}
              {row.type === 'area' && (
                <div className="text-gray-400">
                 ({row.acronym}) {row.name} • Comisión: {row.commission}%
                </div>
              )}
            </div>
          </Box>
        );
      },
    },
    {
      field: 'commission',
      headerName: 'Comisión',
      flex: 1,
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams<BusinessRowData>) => {
        const row = params.row;

        if (row.type === 'area') {
          return (
            <div>
              <div className="text-semibold text-blue-600">
                {row.commission?.toFixed(1)}%
              </div>
              <div className="text-gray-400 text-xs">Área</div>
            </div>
          );
        }

        // Para empresas, mostrar información de áreas
        const business = row.originalBusiness;
        if (!business) return <div className="text-gray-400">—</div>;

        const areaCount = business.areas?.length || 0;

        return (
          <div>
            <div className="text-semibold text-gray-600">
              {areaCount} área{areaCount !== 1 ? 's' : ''}
            </div>
            <div className="text-gray-400 text-xs">Empresa</div>
          </div>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      cellClassName: 'flex items-center justify-end p-8',
      renderCell: (params: GridRenderCellParams<BusinessRowData>) => {
        const row = params.row;

        if (row.type === 'area') {
          return (
            <div className="flex gap-8">
              <IconButton
                aria-label="edit-area"
                onClick={() => selectAreaForEdit(row)}
                size="small"
                sx={{ color: 'primary.main' }}
                title="Editar área"
              >
                <EditIcon />
              </IconButton>
            </div>
          );
        }

        return (
          <div className="flex gap-8">
            <IconButton
              aria-label="add-area"
              onClick={() => selectBusinessForArea(row)}
              size="small"
              sx={{
                color: 'success.main',
              }}
              title="Agregar área"
            >
              <AddIcon />
            </IconButton>
            <IconButton
              aria-label="edit"
              onClick={() => selectBusiness(row)}
              size="small"
              title="Editar empresa"
            >
              <EditIcon />
            </IconButton>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Page
        header={<Header />}
        content={
          <div className="w-full my-16 px-24 md:px-32">
            <Paper className="flex flex-col flex-auto items-center shadow rounded-2xl overflow-hidden">
              <div className="w-full p-32">
                {gridReady && (
                  <Box sx={{ height: '70vh', overflow: 'auto' }}>
                    <DataGrid
                      rows={tableRows}
                      columns={columns}
                      sx={{ border: 0 }}
                      disableColumnSorting
                      disableColumnFilter
                      disableRowSelectionOnClick
                      disableMultipleRowSelection
                      hideFooter
                      getRowHeight={() => 'auto'}
                      pagination={undefined}
                      getRowId={(row) => row.id}
                    />
                  </Box>
                )}
              </div>
            </Paper>
          </div>
        }
        scroll={isMobile ? 'normal' : 'content'}
      />
      <CreateDialog />
      <UpdateDialog />
      <CreateAreaDialog selectedBusiness={selectedBusinessForArea} />
      <UpdateAreaDialog
        selectedBusiness={selectedBusinessForEditArea}
        selectedArea={selectedAreaForEdit}
      />
    </>
  );
};

export default Businesses;
